/**
 * Micheals Infrastructure Advisory - TECHNICAL DESIGN & ARCHITECTURE
 * VERSION: 6.1.0 (Governance & Forensic Design Interface)
 * FILE: src/pages/TechnicalDesign.js
 * ROLE: Logic-first architectural mapping and infrastructure governance.
 */

"use client";

import React, { useRef } from 'react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { Network, Activity, TrendingDown, ShieldCheck, FileSearch } from 'lucide-react';

// Components
import SEO from '../components/SEO';

const TechnicalDesign = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ 
    target: containerRef, 
    offset: ["start start", "end start"] 
  });
  
  const yHero = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scaleXProgress = useSpring(useScroll().scrollYProgress, { 
    stiffness: 400, 
    damping: 90 
  });

  return (
    <div className="bg-white min-h-screen selection:bg-amber-100 overflow-x-hidden">
      <SEO 
        title="Technical Design | Micheals Infrastructure Advisory" 
        description="Strategic systems architecture and logic-first infrastructure oversight." 
      />

      <motion.div 
        className="fixed top-0 left-0 right-0 h-1 bg-amber-800 origin-left z-[2000]" 
        style={{ scaleX: scaleXProgress }} 
      />

      <header 
        ref={containerRef} 
        className="relative w-full h-[70vh] flex flex-col items-center justify-center bg-stone-950 overflow-hidden"
      >
        <motion.div style={{ y: yHero }} className="relative z-10 text-center px-6">
          <span className="text-amber-800 text-[10px] font-black uppercase tracking-[0.6em] mb-8 block">
            Architectural Governance
          </span>
          <h1 className="text-6xl md:text-[7vw] font-serif text-white italic leading-none tracking-tighter">
            Logic-First <span className="not-italic font-light text-stone-500">Systems.</span>
          </h1>
          <p className="max-w-2xl mx-auto mt-10 text-stone-400 text-lg font-light leading-relaxed">
            We operate at the nexus of technical strategy and physical reality. 
            Our architectural mapping ensures your asset’s infrastructure is 
            engineered for absolute performance and long-term fiduciary security.
          </p>
        </motion.div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          <div className="space-y-4">
            <Network className="text-amber-800" size={24} />
            <h3 className="text-sm font-black uppercase tracking-widest text-stone-950">Topology Design</h3>
            <p className="text-stone-600 font-light leading-relaxed text-sm">
              Architectural mapping of digital and physical backbones, prioritizing systemic interoperability and lifecycle resilience.
            </p>
          </div>
          
          <div className="space-y-4">
            <Activity className="text-amber-800" size={24} />
            <h3 className="text-sm font-black uppercase tracking-widest text-stone-950">Performance Auditing</h3>
            <p className="text-stone-600 font-light leading-relaxed text-sm">
              Continuous diagnostic oversight ensuring that field implementation strictly adheres to high-fidelity design specifications.
            </p>
          </div>

          <div className="space-y-4">
            <FileSearch className="text-amber-800" size={24} />
            <h3 className="text-sm font-black uppercase tracking-widest text-stone-950">Forensic Mapping</h3>
            <p className="text-stone-600 font-light leading-relaxed text-sm">
              Stress-testing design logic against potential failure vectors to identify latent vulnerabilities before implementation.
            </p>
          </div>

          <div className="space-y-4">
            <TrendingDown className="text-amber-800" size={24} />
            <h3 className="text-sm font-black uppercase tracking-widest text-stone-950">Capital Protection</h3>
            <p className="text-stone-600 font-light leading-relaxed text-sm">
              Forensic ROI analysis aimed at eliminating technical debt and maximizing the value of your infrastructure portfolio.
            </p>
          </div>

        </div>

        {/* Closing Note */}
        <div className="mt-32 p-12 border border-stone-100 bg-stone-50 rounded-sm">
          <div className="flex items-start gap-6">
            <ShieldCheck className="text-amber-800 mt-1" size={32} />
            <div className="max-w-2xl">
              <h4 className="text-lg font-serif italic text-stone-950 mb-2">Independent Technical Authority</h4>
              <p className="text-stone-600 text-sm leading-relaxed">
                As a consultative firm, our technical fidelity is maintained through complete independence. 
                We serve only the Principal, providing the forensic data required to make high-stakes 
                infrastructure decisions with certainty.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TechnicalDesign;